import { useEffect, useRef } from 'react'
import { io } from 'socket.io-client'

/**
 * useVisionSocket — subscribes to real-time vision events.
 * Server emits `vision:changed` with `{ scope: 'state'|'show'|'jury'|'vote' }`.
 */
export function useVisionSocket(onChange) {
  const cbRef = useRef(onChange)
  useEffect(() => { cbRef.current = onChange }, [onChange])

  useEffect(() => {
    // Ontvanger-pagina's (OBS-browser-source op /kempenvision/stage) hebben
    // geen token — anonieme handshake toestaan, backend accepteert dat.
    const token = localStorage.getItem('auth_token') || undefined
    const s = io({
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    })
    const subscribe = () => s.emit('vision:subscribe')
    s.on('connect', subscribe)
    s.on('reconnect', subscribe)
    s.on('vision:changed', (payload) => {
      try { cbRef.current?.(payload) } catch (e) { console.error('vision:changed handler', e) }
    })
    return () => {
      try { s.emit('vision:unsubscribe') } catch {}
      s.removeAllListeners()
      s.disconnect()
    }
  }, [])
}

/**
 * useStageAudioSync — realtime audio position sync tussen stage en control.
 * Stage callt emitTick met {position, duration, playing} elke seconde.
 * Control ontvangt via onTick. Control kan emitSeek({time}) sturen.
 * Stage ontvangt via onSeek → audioRef.currentTime = time.
 */
export function useStageAudioSync({ onTick, onSeek } = {}) {
  const socketRef = useRef(null)
  const cbRef = useRef({ onTick, onSeek })
  useEffect(() => { cbRef.current = { onTick, onSeek } }, [onTick, onSeek])

  useEffect(() => {
    // Ontvanger-pagina's (OBS-browser-source op /kempenvision/stage) hebben
    // geen token — anonieme handshake toestaan, backend accepteert dat.
    const token = localStorage.getItem('auth_token') || undefined
    const s = io({
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnection: true,
    })
    socketRef.current = s
    const subscribe = () => s.emit('vision:subscribe')
    s.on('connect', subscribe)
    s.on('reconnect', subscribe)
    s.on('stage:tick', (data) => { try { cbRef.current.onTick?.(data) } catch {} })
    s.on('stage:seek', (data) => { try { cbRef.current.onSeek?.(data) } catch {} })
    return () => {
      s.removeAllListeners()
      s.disconnect()
      socketRef.current = null
    }
  }, [])

  return {
    emitTick: (data) => socketRef.current?.emit('stage:tick', data),
    emitSeek: (data) => socketRef.current?.emit('stage:seek', data),
  }
}
